import React, { useState, useEffect, useCallback } from 'react';
import { leaveAPI } from '../../lib/api';
import { formatDate, formatDateTime } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { CalendarOff, Plus, Check, X, Clock } from 'lucide-react';
import { toast } from 'sonner';

const LEAVE_TYPES = [
  { value: 'casual', label: 'Casual Leave' },
  { value: 'medical', label: 'Medical Leave' },
  { value: 'other', label: 'Other' },
];

const LeaveManagement = () => {
  const { user, hasRole } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [formData, setFormData] = useState({
    leave_type: '',
    start_date: '',
    end_date: '',
    reason: '',
  });

  const isAdmin = hasRole(['super_admin', 'admin']);

  const fetchLeaves = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const response = await leaveAPI.getAll(params);
      setLeaves(response.data);
    } catch (error) {
      toast.error('Failed to load leaves');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);


  useEffect(() => {
    fetchLeaves();
  }, [statusFilter, fetchLeaves]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.reason.length > 140) {
      toast.error('Reason must be 140 characters or less');
      return;
    }
    
    try {
      await leaveAPI.create(formData);
      toast.success('Leave request submitted');
      setDialogOpen(false);
      setFormData({ leave_type: '', start_date: '', end_date: '', reason: '' });
      fetchLeaves();
    } catch (error) {
      const errorMsg = error.response?.data?.detail 
        ? (Array.isArray(error.response.data.detail) 
            ? error.response.data.detail.map(e => e.msg || e).join(', ')
            : error.response.data.detail)
        : 'Failed to submit leave request';
      toast.error(errorMsg);
    }
  };

  const handleApprove = async (leaveId) => {
    try {
      await leaveAPI.approve(leaveId);
      toast.success('Leave approved');
      fetchLeaves();
    } catch (error) {
      toast.error('Failed to approve leave');
    }
  };

  const openRejectDialog = (leave) => {
    setSelectedLeave(leave);
    setRejectionReason('');
    setRejectDialogOpen(true);
  };

  const handleReject = async () => {
    try {
      await leaveAPI.reject(selectedLeave.id, { reason: rejectionReason });
      toast.success('Leave rejected');
      setRejectDialogOpen(false);
      fetchLeaves();
    } catch (error) {
      toast.error('Failed to reject leave');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      cancelled: 'bg-slate-100 text-slate-800',
    };
    return styles[status] || 'bg-slate-100 text-slate-800';
  };

  const getLeaveTypeLabel = (type) => {
    const labels = { casual: 'Casual Leave', medical: 'Medical Leave', other: 'Other' };
    return labels[type] || type;
  };

  return (
    <div data-testid="leave-management-page">
      <header className="page-header glass-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Leave Management</h1>
            <p className="text-slate-500">{isAdmin ? 'Manage staff leave requests' : 'Submit and track your leave requests'}</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-red-500 hover:bg-red-600 rounded-full" data-testid="request-leave-btn">
                <Plus className="w-4 h-4 mr-2" />
                Request Leave
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-[Manrope]">Request Leave</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <Label>Leave Type *</Label>
                  <Select value={formData.leave_type} onValueChange={(v) => setFormData({...formData, leave_type: v})}>
                    <SelectTrigger data-testid="select-leave-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent position="popper" sideOffset={5}>
                      {LEAVE_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Start Date *</Label>
                    <Input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                      required
                      data-testid="input-start-date"
                    />
                  </div>
                  <div>
                    <Label>End Date *</Label>
                    <Input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                      min={formData.start_date}
                      required
                      data-testid="input-end-date"
                    />
                  </div>
                </div>
                <div>
                  <Label>Reason * <span className="text-slate-400 text-xs">({formData.reason.length}/140)</span></Label>
                  <Textarea
                    value={formData.reason}
                    onChange={(e) => setFormData({...formData, reason: e.target.value.slice(0, 140)})}
                    placeholder="Enter reason for leave"
                    maxLength={140}
                    required
                    data-testid="input-reason"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-red-500 hover:bg-red-600" data-testid="submit-leave">Submit Request</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <div className="page-content">
        <Card>
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold font-[Manrope] flex items-center gap-2">
                <CalendarOff className="w-5 h-5 text-red-500" />
                {isAdmin ? 'All Leave Requests' : 'My Leave Requests'}
              </CardTitle>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={5}>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading...</div>
            ) : leaves.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No leave requests found.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Leave ID</TableHead>
                    {isAdmin && <TableHead>Staff</TableHead>}
                    <TableHead>Type</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Requested</TableHead>
                    {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leaves.map((leave) => (
                    <TableRow key={leave.id} data-testid={`leave-row-${leave.id}`}>
                      <TableCell className="font-mono text-sm">{leave.leave_id}</TableCell>
                      {isAdmin && (
                        <TableCell>
                          <div>
                            <p className="font-medium">{leave.user_name}</p>
                            <p className="text-xs text-slate-500 capitalize">{leave.user_role?.replace('_', ' ')}</p>
                          </div>
                        </TableCell>
                      )}
                      <TableCell>{getLeaveTypeLabel(leave.leave_type)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3 text-slate-400" />
                          <span>{formatDate(leave.start_date)} - {formatDate(leave.end_date)}</span>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-xs truncate">{leave.reason}</TableCell>
                      <TableCell>
                        <Badge className={getStatusBadge(leave.status)}>{leave.status}</Badge>
                      </TableCell>
                      <TableCell>{formatDateTime(leave.created_at)}</TableCell>
                      {isAdmin && (
                        <TableCell className="text-right">
                          {leave.status === 'pending' && (
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleApprove(leave.id)}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                data-testid={`approve-${leave.id}`}
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openRejectDialog(leave)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`reject-${leave.id}`}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">Reject Leave Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Reason for Rejection</Label>
              <Textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter reason for rejection"
                data-testid="rejection-reason"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
              <Button className="bg-red-500 hover:bg-red-600" onClick={handleReject} data-testid="confirm-reject">Reject</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeaveManagement;
